/**
 * <s-live-chat>: routes every contact, support and pre-sale CTA to the Crisp live chat.
 * Rendered once in layout/theme.liquid. Listens for clicks on:
 *   - any element with [data-live-chat] (explicit chat buttons), and
 *   - any link to the contact pages (/pages/contact, /pages/contact-us, /pages/contact-support),
 *     unless the link carries data-live-chat="off", sits in a menu (header nav, megamenu, mobile drawer,
 *     footer menus: merchant 2026-11-03, menus open the contact page), or points at the page the visitor is already on
 *     (the contact page's own #contact-form routes keep driving the form).
 *
 * Topic = data-live-chat-topic, else the link's ?topic=, else "support" for /pages/contact-support.
 * "form" reads the topic chip picked in the contact form. The topic goes to Crisp as a session segment.
 *
 * The chat composer is prefilled with a message for that topic and page context: the element's
 * data-kind (product, collection, cart, guide…) and data-subject (product / collection / guide title)
 * pick one of the data-msg-* strings (locale `live_chat.*`); "{{ subject }}" is replaced with the title.
 * A CTA can set its own text with data-live-chat-message.
 * If Crisp never loads (blocked or offline), the visitor is sent to the link's page as before.
 */
const CONTACT_PATH = /\/pages\/contact(?:-us|-support)?\/?$/;
const SUBJECT = '{{ subject }}';
const NAV_LINKS = 'nav, .mnav-panel, .nav-mega, [data-desktop-nav]';

class SLiveChat extends HTMLElement {
  #onClick = (event) => this.#handle(event);

  connectedCallback() {
    document.addEventListener('click', this.#onClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onClick, true);
  }

  #handle(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const el = event.target.closest('[data-live-chat], a[href]');
    if (!el || el.dataset.liveChat === 'off') return;

    let url = null;
    if (el instanceof HTMLAnchorElement) {
      try {
        url = new URL(el.href, location.href);
      } catch {
        return;
      }
      if (!('liveChat' in el.dataset)) {
        if (url.origin !== location.origin || !CONTACT_PATH.test(url.pathname)) return;
        // Menu links (header, megamenu, mobile drawer panels, footer menus) always open the contact page.
        if (el.closest(NAV_LINKS)) return;
        if (url.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '')) return;
      }
    }

    if (!window.$crisp) return; // chat not configured: keep the normal link
    event.preventDefault();
    event.stopPropagation();

    const topic = this.#topic(el, url);
    this.#open(topic, el.dataset.liveChatMessage || this.#message(topic), url);
  }

  #topic(el, url) {
    let topic = el.dataset.liveChatTopic || url?.searchParams.get('topic') || '';
    if (!topic && url && /\/pages\/contact-support\/?$/.test(url.pathname)) topic = 'support';
    if (topic === 'form') {
      const checked = document.querySelector('input[data-topic-radio]:checked');
      topic = checked instanceof HTMLInputElement ? checked.dataset.topicRadio || '' : '';
    }
    return topic;
  }

  #message(topic) {
    const { kind = '', subject = '' } = this.dataset;
    const msg = this.dataset;
    const onProduct = kind === 'product' && subject;
    let text;
    switch (topic) {
      case 'presale':
        if (kind === 'cart') text = msg.msgPresaleCart;
        else if (subject && (kind === 'product' || kind === 'collection')) text = msg.msgPresaleSubject;
        else text = msg.msgPresale;
        break;
      case 'support':
        if (kind === 'guide' && subject) text = msg.msgSupportGuide;
        else if (onProduct) text = msg.msgSupportSubject;
        else text = msg.msgSupport;
        break;
      case 'custom':
        text = onProduct ? msg.msgCustomSubject : msg.msgCustom;
        break;
      case 'partner':
        text = msg.msgPartner;
        break;
      default:
        if (kind === 'guide' && subject) text = msg.msgSupportGuide;
        else if (onProduct) text = msg.msgGeneralSubject;
        else text = msg.msgGeneral;
    }
    return (text || msg.msgGeneral || '').split(SUBJECT).join(subject);
  }

  #open(topic, message, fallbackUrl) {
    const crisp = window.$crisp;
    if (topic) crisp.push(['set', 'session:segments', [[topic]]]);
    if (message) crisp.push(['set', 'message:text', [message]]);
    crisp.push(['do', 'chat:show']);
    crisp.push(['do', 'chat:open']);
    if (typeof crisp.is === 'function' || !fallbackUrl) return;
    // Crisp still loading: give it a moment, then fall back to the contact page.
    window.setTimeout(() => {
      if (typeof window.$crisp?.is !== 'function') location.href = fallbackUrl.href;
    }, 3000);
  }
}

if (!customElements.get('s-live-chat')) customElements.define('s-live-chat', SLiveChat);
